/**
 * Structural CSS for <sysml-tree> inside a Lit shadow DOM host.
 *
 * Targets the component's DOM structure directly instead of relying on
 * Tailwind utility classes (which can't cross the shadow DOM boundary).
 *
 * DOM structure per row:
 *   Folder: .tree-row > [button(chevron)] [div(icon)] [span(name)] [button(more)]
 *   File:   .tree-row > [div(spacer)]     [div(icon)] [div(name+tags)] [button(more)]
 */
import { css } from 'lit';

export const treeStyles = css`
  /* ── Root ─────────────────────────────────── */
  sysml-tree {
    display: block;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    font-size: 12px;
    color: var(--r5-text);
  }
  sysml-tree::-webkit-scrollbar {
    width: 8px;
  }
  sysml-tree::-webkit-scrollbar-track {
    background: transparent;
  }
  sysml-tree::-webkit-scrollbar-thumb {
    background-color: rgba(121, 121, 121, 0.4);
  }
  sysml-tree::-webkit-scrollbar-thumb:hover {
    background-color: rgba(100, 100, 100, 0.7);
  }
  sysml-tree::-webkit-scrollbar-corner {
    background: transparent;
  }

  sysml-tree ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  /* ── Row layout ──────────────────────────── */
  sysml-tree .tree-row {
    position: relative;
    display: flex;
    align-items: center;
    height: 1.75rem;
    border-radius: 0.125rem;
    user-select: none;
    cursor: pointer;
  }

  sysml-tree .tree-row:hover {
    background: color-mix(in srgb, var(--r5-muted) 20%, transparent);
  }

  sysml-tree .tree-row[data-focused="true"] {
    background: color-mix(in srgb, var(--r5-muted) 40%, transparent);
    outline: 2px solid var(--r5-primary, #3b82f6);
    outline-offset: -2px;
  }

  sysml-tree .tree-row[data-dragging="true"] {
    opacity: 0.5;
  }

  /* ── Buttons (chevron toggle, more-actions): 24×24 ── */
  sysml-tree .tree-row > button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    border: none;
    background: none;
    color: var(--r5-text-muted);
    cursor: pointer;
    flex-shrink: 0;
    border-radius: 0.125rem;
  }

  sysml-tree .tree-row > button:hover {
    color: var(--r5-text);
  }

  sysml-tree .tree-row > button > svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  /* More-actions button: hidden until row hover */
  sysml-tree .tree-row > button:last-child {
    opacity: 0;
    transition: opacity 150ms ease;
    margin-right: 0.25rem;
  }
  sysml-tree .tree-row:hover > button:last-child,
  sysml-tree .tree-row[data-focused="true"] > button:last-child {
    opacity: 1;
  }
  sysml-tree .tree-row > button:last-child:hover {
    background: color-mix(in srgb, var(--r5-muted) 30%, transparent);
  }

  /* ── Spacer div (file rows, where chevron would be) ── */
  sysml-tree .tree-row > div[aria-hidden="true"] {
    width: 1.5rem;
    height: 1.5rem;
    flex-shrink: 0;
  }

  /* ── Icon container div (has SVG direct child, NOT a text span) ── */
  sysml-tree .tree-row > div:has(> svg) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    color: var(--r5-text-muted);
  }

  sysml-tree .tree-row > div:has(> svg) > svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  /* Domain-specific icons injected via innerHTML (Lucide SVGs) */
  sysml-tree .tree-type-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    color: var(--r5-text-muted);
  }

  sysml-tree .tree-type-icon > svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  /* ── Folder name (direct span child of row) ── */
  sysml-tree .tree-row > span {
    font-size: 12px;
    color: var(--r5-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-left: 0.375rem;
    flex: 1 1 0%;
    min-width: 0;
  }

  /* ── File name + tags container (div with span child) ── */
  sysml-tree .tree-row > div:has(> span) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
    flex: 1 1 0%;
    margin-left: 0.375rem;
    overflow: hidden;
  }

  /* File name text */
  sysml-tree .tree-row > div:has(> span) > span:first-child {
    font-size: 12px;
    color: var(--r5-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  /* Tags & timestamp row (shown on hover) */
  sysml-tree .tree-row > div:has(> span) > div {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 150ms ease;
  }

  sysml-tree .tree-row:hover > div:has(> span) > div {
    opacity: 1;
  }

  /* ── Selected file row ────────────────────── */
  sysml-tree li[aria-selected="true"] > .tree-row {
    background: color-mix(in srgb, var(--r5-primary) 15%, transparent);
  }

  sysml-tree li[aria-selected="true"] > .tree-row > span,
  sysml-tree li[aria-selected="true"] > .tree-row > div:has(> span) > span:first-child {
    color: var(--r5-primary);
  }

  /* ── Drop indicators ──────────────────────── */
  sysml-tree .drop-indicator {
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--r5-primary, #3b82f6);
    pointer-events: none;
    z-index: 10;
  }
  sysml-tree .drop-indicator--before { top: -1px; }
  sysml-tree .drop-indicator--after { bottom: -1px; }
  sysml-tree .drop-into {
    background: rgba(59, 130, 246, 0.15) !important;
  }

  /* ── Checkbox ─────────────────────────────── */
  sysml-tree .tree-checkbox {
    width: 1.25rem;
    height: 1.25rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    margin-right: 0.25rem;
  }
  sysml-tree .tree-checkbox svg { width: 1rem; height: 1rem; }
  sysml-tree .tree-checkbox--checked { color: var(--r5-primary, #3b82f6); }
  sysml-tree .tree-checkbox--indeterminate { color: var(--r5-primary, #3b82f6); }
  sysml-tree .tree-checkbox--unchecked { color: var(--r5-border, #334155); }
  sysml-tree .tree-checkbox--unchecked .tree-checkbox__empty { display: block; }
  sysml-tree .tree-checkbox--unchecked .tree-checkbox__hover { display: none; }
  sysml-tree .tree-checkbox--unchecked:hover { color: var(--r5-primary, #3b82f6); }
  sysml-tree .tree-checkbox--unchecked:hover .tree-checkbox__empty { display: none; }
  sysml-tree .tree-checkbox--unchecked:hover .tree-checkbox__hover { display: block; }

  /* ── Tag badge colors ─────────────────────── */
  sysml-tree .bg-blue-500\\/10 { background-color: rgb(59 130 246 / 0.1); }
  sysml-tree .text-blue-400 { color: rgb(96 165 250); }
  sysml-tree .border-blue-500\\/30 { border-color: rgb(59 130 246 / 0.3); }
  sysml-tree .bg-green-500\\/10 { background-color: rgb(34 197 94 / 0.1); }
  sysml-tree .text-green-400 { color: rgb(74 222 128); }
  sysml-tree .border-green-500\\/30 { border-color: rgb(34 197 94 / 0.3); }
  sysml-tree .bg-purple-500\\/10 { background-color: rgb(168 85 247 / 0.1); }
  sysml-tree .text-purple-400 { color: rgb(192 132 252); }
  sysml-tree .border-purple-500\\/30 { border-color: rgb(168 85 247 / 0.3); }
  sysml-tree .bg-orange-500\\/10 { background-color: rgb(249 115 22 / 0.1); }
  sysml-tree .text-orange-400 { color: rgb(251 146 60); }
  sysml-tree .border-orange-500\\/30 { border-color: rgb(249 115 22 / 0.3); }
  sysml-tree .bg-red-500\\/10 { background-color: rgb(239 68 68 / 0.1); }
  sysml-tree .text-red-400 { color: rgb(248 113 113); }
  sysml-tree .border-red-500\\/30 { border-color: rgb(239 68 68 / 0.3); }
  sysml-tree .bg-gray-500\\/10 { background-color: rgb(107 114 128 / 0.1); }
  sysml-tree .text-gray-400 { color: rgb(156 163 175); }
  sysml-tree .border-gray-500\\/30 { border-color: rgb(107 114 128 / 0.3); }
`;
